import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Icon from './Icon';
import { localRect, localViewport } from '../../utils/zoom';

/**
 * Compact select styled to the app rather than the OS.
 *
 * A native <select> renders its list with the platform's own chrome, which on
 * the dark theme means a light system menu with system fonts — the one control
 * that ignores the design tokens. This reimplements the listbox with the same
 * surface/line/accent treatment the rest of the design system uses.
 *
 * The menu is portalled to <body>: it is often opened from inside a block whose
 * scroll container would otherwise clip it.
 *
 * options: [{ value, label, badge?, dividerAfter? }] — `value` is compared with
 * ===, so '' is a fine "none" entry. `badge` prints a micro-label above the
 * option and tints the row, mirroring the role bars on DatasetSummary's column
 * pills; `dividerAfter` rules a line under it to close off a leading group.
 *
 * `multiple`: `value` is an array, picking toggles an entry and keeps the menu
 * open. `icon`: the trigger is just that icon (tinted, with a count, while
 * anything is selected), for filters that sit beside a label.
 */

const MENU_MAX_HEIGHT = 260;
const MENU_MIN_WIDTH = 150;
const GAP = 4;

const Select = ({
  value,
  onChange,
  options = [],
  ariaLabel,
  disabled = false,
  maxWidth = 190,
  multiple = false,
  icon,
}) => {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  // Keyboard cursor. Kept separate from `value` so arrowing never commits.
  const [activeIndex, setActiveIndex] = useState(-1);
  const baseId = useMemo(() => `q-sel-${Math.random().toString(36).slice(2, 9)}`, []);

  const isSelected = (opt) => (multiple ? value.includes(opt.value) : opt.value === value);
  const selectedIndex = options.findIndex(isSelected);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;
  const active = multiple ? value.length > 0 : !!selected && selected.value !== '';
  const triggerLabel = multiple ? options.filter(isSelected).map(o => o.label).join(', ') : selected?.label;

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = localRect(el);
    const vp = localViewport();
    const width = Math.max(r.width, MENU_MIN_WIDTH);
    const below = vp.height - r.bottom - GAP - 8;
    const above = r.top - GAP - 8;
    // Flip up only when below genuinely cannot hold a usable list.
    const flip = below < Math.min(MENU_MAX_HEIGHT, 120) && above > below;
    setPos({
      left: Math.max(8, Math.min(r.left, vp.width - width - 8)),
      top: flip ? undefined : r.bottom + GAP,
      bottom: flip ? vp.height - r.top + GAP : undefined,
      width,
      maxHeight: Math.min(MENU_MAX_HEIGHT, flip ? above : below),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    window.addEventListener('resize', place);
    // Capture phase: any ancestor scrolling moves the trigger too.
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  // Open with the cursor on the current selection so arrows continue from it.
  // Only on opening: in `multiple` mode the selection changes while open and
  // the cursor must stay on the row just toggled.
  useEffect(() => {
    if (open) setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Keep the keyboard cursor in view in a scrolled list.
  useEffect(() => {
    if (!open || activeIndex < 0 || !menuRef.current) return;
    const node = menuRef.current.querySelector(`[data-idx="${activeIndex}"]`);
    if (node && node.scrollIntoView) node.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const commit = (opt) => {
    if (multiple) {
      if (opt) onChange?.(isSelected(opt) ? value.filter(v => v !== opt.value) : [...value, opt.value]);
      return;
    }
    setOpen(false);
    triggerRef.current?.focus();
    if (opt && opt.value !== value) onChange?.(opt.value);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); triggerRef.current?.focus(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(options.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(0, i - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setActiveIndex(0); }
    else if (e.key === 'End') { e.preventDefault(); setActiveIndex(options.length - 1); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); commit(options[activeIndex]); }
    else if (e.key === 'Tab') setOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-controls={open ? `${baseId}-list` : undefined}
        disabled={disabled}
        onClick={() => !disabled && setOpen(v => !v)}
        onKeyDown={onKeyDown}
        title={icon ? triggerLabel || undefined : undefined}
        style={icon ? {
          display: 'inline-flex', alignItems: 'center', gap: 2,
          padding: '2px 4px', borderRadius: 'var(--r-sm)', border: 'none',
          background: open || active ? 'var(--accent-soft)' : 'transparent',
          color: open || active ? 'var(--accent)' : 'var(--fg-subtle)',
          fontFamily: 'var(--font-sans)', fontSize: 10.5, fontWeight: 600, lineHeight: 1,
          cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
          transition: 'background 120ms, color 120ms',
        } : {
          display: 'inline-flex', alignItems: 'center', gap: 6,
          maxWidth, padding: '4px 8px', borderRadius: 'var(--r-sm)',
          background: open ? 'var(--accent-soft)' : 'var(--bg-elev-2)',
          border: `1px solid ${open ? 'var(--line-bright)' : 'var(--line-soft)'}`,
          color: active ? 'var(--accent)' : 'var(--fg)',
          fontFamily: 'var(--font-sans)', fontSize: 11.5, lineHeight: 1.4,
          cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
          transition: 'background 120ms, border-color 120ms',
        }}
      >
        {icon ? (
          <>
            <Icon name={icon} size={12} />
            {multiple && active && <span>{value.length}</span>}
          </>
        ) : (
          <>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {triggerLabel || ''}
            </span>
            <Icon
              name="chevron-down"
              size={13}
              style={{
                marginLeft: 'auto', opacity: 0.7,
                transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 140ms',
              }}
            />
          </>
        )}
      </button>

      {open && pos && ReactDOM.createPortal(
        <div
          ref={menuRef}
          id={`${baseId}-list`}
          role="listbox"
          aria-label={ariaLabel}
          aria-multiselectable={multiple || undefined}
          style={{
            position: 'fixed', left: pos.left, top: pos.top, bottom: pos.bottom,
            width: pos.width, maxHeight: pos.maxHeight, overflowY: 'auto',
            zIndex: 1300, padding: 4,
            background: 'var(--surface-solid)',
            border: '1px solid var(--line)', borderRadius: 'var(--r-md)',
            boxShadow: 'var(--shadow-glass)', fontFamily: 'var(--font-sans)',
          }}
        >
          {options.map((opt, i) => {
            const selectedOpt = isSelected(opt);
            const isActive = i === activeIndex;
            // A badged row is already tinted, so its hover cue has to be a step
            // up from that tint rather than the plain-row elevation.
            const background = opt.badge
              ? (isActive ? 'color-mix(in srgb, var(--accent) 12%, transparent)' : 'var(--accent-soft-2)')
              : (isActive ? 'var(--bg-elev-1)' : 'transparent');
            return (
              <div
                key={opt.value}
                data-idx={i}
                role="option"
                aria-selected={selectedOpt}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => commit(opt)}
                title={opt.label}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: opt.badge ? '4px 8px 5px' : '5px 8px',
                  borderRadius: 'var(--r-sm)', cursor: 'pointer',
                  fontSize: 11.5, lineHeight: 1.4,
                  background,
                  color: selectedOpt || opt.badge ? 'var(--accent)' : 'var(--fg)',
                  fontWeight: selectedOpt ? 600 : 400,
                  // Sits under the row, inside the menu's padding, so it reads as
                  // a group separator rather than a border on the row itself.
                  ...(opt.dividerAfter
                    ? { marginBottom: 4, paddingBottom: 5, borderBottom: '1px solid var(--line)' }
                    : null),
                }}
              >
                <span style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
                  {opt.badge && (
                    <span style={{
                      display: 'block',
                      fontSize: 7.5, fontWeight: 700, letterSpacing: '0.05em', lineHeight: 1.35,
                      color: 'var(--accent)',
                    }}>
                      {opt.badge}
                    </span>
                  )}
                  <span style={{
                    display: 'block',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {opt.label}
                  </span>
                </span>
                {selectedOpt && <Icon name="check" size={13} />}
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
};

export default Select;
